import React, { useEffect, useState } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import {
  createOrUpdateClass,
  createOrUpdateClassFn,
  createOrUpdateTopic,
  createOrUpdateTopicFn,
  getAllTopics,
  getAllTopicsFn,
  queryClasses,
  queryClassesFn,
} from 'store/pdp/pdpSlice';
import APopover from 'components/APopover';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Input from 'components/Input';
import { TopicEditor } from './TopicEditor';
import Select, { IOption } from '../../../../components/Select/Select';
import { TagListContainer } from './style';
import { messages } from './Messages';
import { RouteComponentProps } from '@reach/router';
import LogoLoading from '../../../../components/LogoLoading/LogoLoading';
import Modal from '../../../../components/Modal';
import { DataContextProvider } from '../../../../store/context/DataContext';
import TaskItemsViewer from '../../TasksManagement/TaskItemsViewer';
import { appDispatch, RootState } from 'store/store';
import { CompanyPDPState, PDPClass, Topic, TopicStatus } from 'store/pdp/types';
import { lightGray } from 'utils/style/colors';
import {
  IProviderItems,
  IProviderItemViewWithData,
  ITags,
  ITagView,
} from 'store/providers/providerTypes';
import {
  ICompany,
  ICompanyProviders,
  ICompanyRole,
} from 'store/companies/companiesTypes';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import { ISurveyItem } from '../../../../store/survey/surveyTypes';
import { requireDocument } from '../../../../store/providers/providerSlice';

interface StateToProps {
  providerItems?: { [key: number]: IProviderItems };
  pdpState: CompanyPDPState;
  tags: ITags['data'];
  company: ICompany;
  relatedProviders: ICompanyProviders;
  companyMembers?: ICompany['members'];
  isSideBarCollapsed: boolean;
}
interface DispatchToProps {
  dispatch?: any;
  requireProviderItem: (providerItemId: number, token?: string) => Promise<any>;
  addORUpdateTopic: (topic: Topic) => ReturnType<typeof createOrUpdateTopicFn>;
  getAllTopics: (pdpId: number) => ReturnType<typeof getAllTopicsFn>;
  getAllClassTemplates: (pdpId: number) => ReturnType<typeof queryClassesFn>;
  createOrUpdateClass: (
    clazz: PDPClass,
    pdpId: number
  ) => ReturnType<typeof createOrUpdateClassFn>;
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
}
interface IOwnProps {
  companyId?: number;
  pdpId?: string;
}
type ComponentProps = StateToProps &
  DispatchToProps &
  IOwnProps &
  RouteComponentProps;
const TopicManager: React.FC<ComponentProps> = (props) => {
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!props.pdpId) return;

    setLoading(true);
    Promise.all([
      props.getAllTopics(Number(props.pdpId)),
      props.getAllClassTemplates(Number(props.pdpId)),
    ]).finally(() => {
      setLoading(false);
    });
  }, [props.pdpId]);
  const [editingClass, setEditingClass] = useState<PDPClass | undefined>(
    undefined
  );

  const [viewFilter, setViewFilter] = useState(
    'Active' as 'Active' | TopicStatus
  );
  const [filterTagIds, setFilterTagIds] = useState([] as number[]);

  const [isOnlyMandatory, setIsOnlyMandatory] = useState<boolean>(false);
  const [isOnlyOptional, setIsOnlyOptional] = useState<boolean>(false);
  const [searchTargetGroup, setSearchTargetGroup] = useState<string>('');

  const [searchValue, setSearchValue] = useState('');
  const filteredTopics = Object.values(props.pdpState?.topics || {})
    .filter(
      (it) =>
        ((viewFilter === 'Active' &&
          it.status !== 'Archived' &&
          it.status !== 'Disabled') ||
          it.status === viewFilter) &&
        !filterTagIds.find((tagId) => !it.properties.tags.includes(tagId)) &&
        it.properties.name.includes(searchValue)
    )
    .sort((a, b) => a.properties.name.localeCompare(b.properties.name));
  const allRoles = Object.values(props.company.roles) as ICompanyRole[];
  const allRoleOptions =
    allRoles
      ?.filter((it) => !it.isProjectRole)
      ?.map((it) => ({ value: it.id, label: it.displayName! })) || [];
  const tagOptions: { [key: number]: IOption[] } = {};
  const selectedPdpId = Number(props.pdpId)!;
  props.company.pdpPanels
    .find((companyPdp) => companyPdp.id === selectedPdpId)!
    .properties.topicTags.forEach((it) => {
      tagOptions[it] = [];
    });

  Object.values(props.tags).forEach((it: ITagView) => {
    if (
      it.parentId &&
      props.company.pdpPanels
        .find((companyPdp) => companyPdp.id === selectedPdpId)!
        .properties.topicTags.includes(it.parentId) &&
      !it.deleted
    ) {
      tagOptions[it.parentId].push({
        value: it.id,
        label: it.text,
      });
    }
  });

  const getProviderItem = (itemId?: number) => {
    let existItem: IProviderItemViewWithData | undefined;
    if (Object.keys(props.providerItems || {}).length !== 0 && itemId) {
      const foundItem = Object.values(props?.relatedProviders || {})?.reduce(
        (c, r) => {
          if (c) return c;
          return props.providerItems![r?.id]?.items[itemId];
        },
        null as any
      );
      if (foundItem) {
        existItem = foundItem;
      }
    }
    return existItem;
  };

  const [viewingItem, setViewingItem] = useState(
    undefined as IProviderItemViewWithData | undefined
  );
  const [loadingItemId, setLoadingItemId] = useState(
    undefined as number | undefined
  );

  const [showAdd, setShowAdd] = useState(false);
  if (loading)
    return (
      <div style={{ margin: 'auto' }}>
        <LogoLoading />
      </div>
    );
  const handleOpenItem = (itemId: number) => {
    const itemInStore = getProviderItem(itemId);

    if (itemInStore && itemId) {
      setViewingItem(itemInStore);
    } else {
      setLoadingItemId(itemId);
      props.requireProviderItem(itemId).then((res) => {
        setLoadingItemId(undefined);
        setViewingItem({ data: res });
      });
    }
  };
  const openItem = (item: ISurveyItem) => {
    setViewingItem({ data: item });
  };
  const availableTopicList = Object.values(props.pdpState?.topics || {})
    .filter((it) => it.status !== 'Archived')
    .reduce(
      (acc, item) => {
        acc.push({ title: item.properties.name, id: item.id! });
        return acc;
      },
      [] as { title: string; id: number }[]
    );
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        padding: '1em',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          margin: '1rem 0',
        }}
      >
        <h5>{messages.topicList}</h5>
        <APopover
          arrow
          curveBorder
          show={showAdd}
          onShowChange={() => {
            /**/
          }}
          target={
            <div>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAdd(!showAdd);
                }}
              >
                <Icon type="plus" />
                {messages.addTopic}
              </Button>
            </div>
          }
        >
          <div>
            <TopicEditor
              new={true}
              allRoleOptions={allRoleOptions}
              selectTagOptions={tagOptions}
              createOrUpdateTopic={props.addORUpdateTopic}
              allTags={props.tags}
              pdpId={selectedPdpId!}
              editPDPClass={() => {
                /*do nothing*/
              }}
              availableTopicList={availableTopicList}
              createPDPClass={() => {
                return Promise.resolve();
              }}
            />
          </div>
        </APopover>
      </div>

      <div style={{ flexGrow: 1, overflow: 'scroll' }}>
        <TagListContainer>
          <thead
            style={{
              position: 'sticky',
              top: '0px',
              zIndex: 100,
              backgroundColor: 'white',
              boxShadow: ' inset 0 -1px 0 #CCC',
              background: lightGray,
            }}
          >
            <tr style={{ boxShadow: ' inset 0 1px 0 #CCC' }}>
              <td
                style={{
                  width: '100px',
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  backgroundColor: lightGray,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                  }}
                >
                  <div>{messages.title}</div>
                </div>
              </td>
              <td>
                <div>{messages.optionalRoles}</div>
              </td>
              <td className="flex flex-col">
                <div>{messages.mandatoryRoles}</div>
              </td>
              {Object.keys(tagOptions).map((it) => {
                return (
                  <td key={it} style={{ width: '200px' }}>
                    <div className="flex flex-col">
                      <div>{props.tags[it]?.text}</div>
                    </div>
                  </td>
                );
              })}
              <td style={{ width: '200px' }}>
                <div>{messages.status}</div>
              </td>
              <td
                style={{
                  width: '50px',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: lightGray,
                }}
                colSpan={2}
              >
                {messages.action}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  width: '250px',
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  backgroundColor: lightGray,
                  verticalAlign: 'top',
                }}
              >
                <Input
                  id={'search'}
                  placeholder={messages.search}
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                  }}
                />
              </td>
              <td colSpan={2} style={{ verticalAlign: 'top' }}>
                <div className="flex flex-col">
                  <Select
                    options={allRoleOptions}
                    values={allRoleOptions.filter(
                      (item) => item?.label === searchTargetGroup
                    )}
                    onChangeValues={(values: IOption[]) => {
                      setSearchTargetGroup(values[0]?.label || '');
                    }}
                    allowRemove={true}
                    multiChoice={false}
                  />
                  <div className="flex gap-4">
                    <Input
                      id={'only-optional'}
                      type="checkbox"
                      checked={isOnlyOptional}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setIsOnlyOptional(e.target.checked);
                        if (e.target.checked) {
                          setIsOnlyMandatory(false);
                        }
                      }}
                      label={messages.onlyOptional}
                    />
                    <Input
                      id={'only-mandatory'}
                      type="checkbox"
                      checked={isOnlyMandatory}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setIsOnlyMandatory(e.target.checked);
                        if (e.target.checked) {
                          setIsOnlyOptional(false);
                        }
                      }}
                      label={messages.onlyMandatory}
                    />
                  </div>
                </div>
              </td>
              {Object.keys(tagOptions).map((it) => {
                return (
                  <td key={it} style={{ width: '200px', verticalAlign: 'top' }}>
                    <div className="flex flex-col">
                      <Select
                        onChangeValues={(values) => {
                          const newTagIds = filterTagIds.filter(
                            (tagId) => props.tags[tagId].parentId !== Number(it)
                          );
                          if (values.length)
                            newTagIds.push(Number(values[0].value));
                          setFilterTagIds(newTagIds);
                        }}
                        options={tagOptions[it]}
                        values={filterTagIds
                          .filter(
                            (tagId) => props.tags[tagId].parentId === Number(it)
                          )
                          .map((it) => ({
                            label: props.tags[it].text,
                            value: it,
                          }))}
                      />
                    </div>
                  </td>
                );
              })}
              <td style={{ verticalAlign: 'top' }}>
                <Select
                  containerStyle={{ width: '150px' }}
                  onChangeValues={(v) => {
                    setViewFilter(v[0].value as any);
                  }}
                  placeholder={messages.status}
                  options={[
                    'Active',
                    'Exists',
                    'Proposed',
                    'Disabled',
                    'Archived',
                  ].map((it) => ({
                    value: it,
                    label: messages[it],
                  }))}
                  allowRemove={false}
                  values={[{ value: viewFilter, label: messages[viewFilter] }]}
                />
              </td>
              <td></td>
            </tr>
          </thead>
          <tbody>
            {filteredTopics.map((it) => {
              const classTemplate = Object.values(
                props.pdpState.classes || {}
              ).find((c) => c.template && c.topicId === it.id);
              const selectedOptionalRoles = allRoleOptions.filter(
                (el) =>
                  it?.properties.relatedToRoles.includes(Number(el.value)) &&
                  !(it?.properties.mandatoryForRoles || []).includes(
                    Number(el.value)
                  )
              );
              const selectedOptionalRolesLabelList =
                selectedOptionalRoles.reduce((acc: string[], item) => {
                  acc.push(item.label);
                  return acc;
                }, []);
              const selectedMandatoryRoles = allRoleOptions.filter(
                (el) =>
                  it?.properties.relatedToRoles.includes(Number(el.value)) &&
                  it?.properties.mandatoryForRoles?.includes(Number(el.value))
              );
              const selectedMandatoryRolesLabelList =
                selectedMandatoryRoles.reduce((acc: string[], item) => {
                  acc.push(item.label);
                  return acc;
                }, []);
              if (
                searchTargetGroup.length > 3 &&
                (!isOnlyOptional && !isOnlyMandatory
                  ? !(
                      selectedOptionalRolesLabelList.includes(
                        searchTargetGroup
                      ) ||
                      selectedMandatoryRolesLabelList.includes(
                        searchTargetGroup
                      )
                    )
                  : isOnlyOptional
                    ? !selectedOptionalRolesLabelList.includes(
                        searchTargetGroup
                      )
                    : !selectedMandatoryRolesLabelList.includes(
                        searchTargetGroup
                      ))
              )
                return <></>;
              return (
                <TopicEditor
                  isSideBarCollapsed={props.isSideBarCollapsed}
                  key={it.id}
                  topic={it}
                  allRoleOptions={allRoleOptions}
                  selectTagOptions={tagOptions}
                  createOrUpdateTopic={props.addORUpdateTopic}
                  allTags={props.tags}
                  pdpId={selectedPdpId!}
                  classTemplate={classTemplate}
                  editPDPClass={() => setEditingClass(classTemplate)}
                  loadingItem={loadingItemId}
                  handleOpenItem={handleOpenItem}
                  handleOpenDirectItem={openItem}
                  availableTopicList={availableTopicList}
                  createPDPClass={() =>
                    props
                      .createOrUpdateClass(
                        {
                          topicId: it.id!,
                          id: undefined as any,
                          template: true,
                          properties: {
                            phaseProps: props.company.pdpPanels.find(
                              (companyPdp) => companyPdp.id === selectedPdpId
                            )!.properties.classPhaseDefaultProps,
                            classDocumentHomeId: undefined as any,
                            classGroups: [],
                            allowChooseGroup: 'No',
                            autoApproveProps: {
                              autoApproveEnabled: false,
                              phaseList: [],
                            },
                          },
                          participants: [],
                          relatedTermIds: [],
                        } as PDPClass,
                        selectedPdpId!
                      )
                      .then((pdpClass: any) => {
                        setEditingClass(classTemplate || pdpClass);
                      })
                  }
                />
              );
            })}
          </tbody>
        </TagListContainer>
      </div>
      {viewingItem && (
        <DataContextProvider
          value={{ providerId: viewingItem.data.providerId }}
        >
          <TaskItemsViewer
            dispatch={props.dispatch}
            onClose={() => setViewingItem(undefined)}
            openItem={viewingItem}
            openItemProps={{}}
          />
        </DataContextProvider>
      )}
      {editingClass && (
        <Modal
          isActive={true}
          title={messages.editingTemplateClassFor(
            props.pdpState!.topics![editingClass.topicId].properties.name
          )}
          onClose={() => setEditingClass(undefined)}
        >
          {/*<ClassTemplateEditor
            templateClass={editingClass}
            company={props.company}
            tags={props.tags}
            relatedProviders={props.relatedProviders}
            saveClass={(clazz) => {
              const pdpClassPromise = props.createOrUpdateClass(
                clazz,
                props.company.pdpPanel.id
              );
              pdpClassPromise.then(() => {
                setEditingClass(undefined);
              });
              return pdpClassPromise;
            }}
            requireSeveralUser={props.requireSeveralUser}
            companyMembers={props.companyMembers}
          />*/}
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps: MapStateToProps<StateToProps, IOwnProps, RootState> = (
  state,
  ownProps
) => {
  const providerIds = Object.values(state.providers.map).reduce(
    (current: number[], it: any) => {
      if (it.data?.managingCompanyId === Number(ownProps.companyId))
        current.push(it.data.id);
      return current;
    },
    [] as number[]
  );
  let tags = {};
  const toLoadTags = [] as number[];
  providerIds.forEach((id: number) => {
    if (state.providers.tags[id] && !state.providers.tags[id]?.status)
      toLoadTags.push(id);
    else
      tags = {
        ...tags,
        ...state.providers.tags[id]?.data,
      };
  });
  const company = state.companies.data[Number(ownProps.companyId)];
  const relatedProviders =
    state.companies.data[Number(ownProps.companyId)].relatedProviders;
  const companyMembers = state.companies.data[company.id].members;
  const providerItems = state.providers.items;
  const isSideBarCollapsed = state.app.UI.sidebar.isSideBarCollapsed;

  const pdpId = Number(ownProps.pdpId);

  return {
    providerItems,
    company,
    relatedProviders,
    tags: tags,
    pdpState: state.pdp.pdpMap[pdpId] || { topics: {}, classes: {} }, // Provide fallback
    companyMembers: companyMembers ?? undefined,
    isSideBarCollapsed,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  DispatchToProps,
  IOwnProps
> = () => {
  return {
    addORUpdateTopic: (topic: Topic) =>
      appDispatch(createOrUpdateTopic({ topic })),
    getAllTopics: (pdpId: number) => appDispatch(getAllTopics({ pdpId })),
    getAllClassTemplates: (pdpId: number) =>
      appDispatch(
        queryClasses({
          pdpId,
          includeDeleted: false,
          isTemplate: true,
        })
      ),
    createOrUpdateClass: (pdpClass: PDPClass, pdpId: number) =>
      appDispatch(createOrUpdateClass({ clazz: pdpClass, pdpId })),
    requireSeveralUser: (usersList: number[]) =>
      appDispatch(requireSeveralUser({ usersList })),
    requireProviderItem: (providerItemId: number, token?: string) =>
      appDispatch(requireDocument({ documentId: providerItemId, token })),
    dispatch: appDispatch,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TopicManager);
